/**
 * Portfolio projects data. Optional fields (image, role, problem, solution, impact, features)
 * are used by the case-study modal; all projects have at least id, title, description, tech, category, featured, highlights.
 */
export const projects = [
  {
    id: 1,
    title: 'HealthPay',
    description: 'A React single-page application for reviewing medical insurance claims, cross-checking extracted JSON data against source PDFs with smart navigation.',
    tech: ['React', 'TypeScript', 'Vite', 'Tailwind CSS', 'react-pdf'],
    github: 'https://github.com/SriramDivi1/Health-Pay',
    features: ['Payment processing', 'User management', 'Responsive UI'],
    learnings: ['React state management', 'Payment flow UI design'],
  },
  {
    id: 2,
    title: 'ItsMyScreen',
    description: 'A real-time, futuristic polling application built with Next.js 16, Supabase, and Tailwind CSS. Create instant polls, vote anonymously, and watch live results.',
    tech: ['Next.js', 'TypeScript', 'Supabase', 'PostgreSQL', 'Tailwind CSS', 'Optimistic UI'],
    github: 'https://github.com/SriramDivi1/ItsMyScreen',
    live: 'https://itsmyscreen-by-sriram.vercel.app',
    category: 'Full-Stack',
    featured: true,
    highlights: [
      'Real-time polling & live results',
      'Next.js 16 + Supabase',
      'Anonymous voting',
      'Optimistic UI updates',
    ],
    role: 'Full-stack developer',
    problem: 'Users need to create and vote in polls with instant, live results.',
    solution: 'Next.js 16 app with Supabase for real-time data and optimistic UI for snappy UX.',
    impact: 'Shows real-time and optimistic UI patterns with a modern stack.',
    image: null,
    features: ['Real-time polling', 'Anonymous voting', 'Live results', 'Optimistic UI'],
    learnings: ['Supabase real-time subscriptions', 'Optimistic UI and rollback on error', 'Next.js 16 App Router patterns'],
  },
  {
    id: 3,
    title: 'Flow',
    description: 'A scalable full-stack productivity dashboard built with React 19, TailwindCSS, and FastAPI. Features secure JWT authentication, real-time updates, and a modern UI.',
    tech: ['React', 'FastAPI', 'PostgreSQL', 'SQLAlchemy', 'JWT', 'Tailwind CSS'],
    github: 'https://github.com/SriramDivi1/Flow',
    live: 'https://flow-app-zeta.vercel.app/',
    category: 'Full-Stack',
    featured: true,
    highlights: [
      'JWT auth & real-time updates',
      'React 19 + FastAPI backend',
      'Responsive dashboard UI',
      'PostgreSQL + SQLAlchemy',
    ],
    role: 'Full-stack developer',
    problem: 'Need a single place to manage tasks and productivity with secure auth and real-time sync.',
    solution: 'Built a full-stack dashboard with React 19 frontend and FastAPI backend, JWT auth, and PostgreSQL.',
    impact: 'Demonstrates end-to-end ownership: API design, auth, DB, and responsive UI.',
    image: null,
    features: ['JWT authentication', 'Real-time updates', 'PostgreSQL + SQLAlchemy', 'Responsive dashboard'],
    longDescription: 'Flow is a full-stack productivity dashboard where I implemented JWT auth, real-time sync, and a clean API with FastAPI and SQLAlchemy. The frontend uses React 19 with a modern, responsive UI.',
    learnings: ['End-to-end JWT flow and token refresh', 'Real-time updates with polling and state sync', 'REST API design with FastAPI and Pydantic'],
  },
  {
    id: 4,
    title: 'Aira Client OS',
    description: 'Aira frontend monorepo — Next.js web dashboard + React Native mobile app (Frontend hiring assignment).',
    tech: ['Next.js', 'React Native', 'TypeScript'],
    github: 'https://github.com/SriramDivi1/aira-client-os',
    category: 'Full-Stack',
    featured: false,
    highlights: ['Web dashboard', 'Mobile application', 'Monorepo setup'],
    role: 'Frontend engineer',
    problem: 'Required a cohesive frontend system across web and mobile.',
    solution: 'Created a Next.js dashboard and React Native mobile App within a monorepo workspace.',
    impact: 'Showcased cross-platform frontend architecture.',
    image: null,
    features: ['Web App', 'Mobile App', 'Shared Types'],
    learnings: ['Monorepo architecture', 'React Native navigation'],
  },
  {
    id: 5,
    title: 'TaskFlow',
    description: 'A modern task management app built with React, TypeScript, and Tailwind CSS featuring intuitive UI, calendar integration, and comprehensive accessibility.',
    tech: ['React', 'TypeScript', 'Vite', 'Tailwind CSS', 'Framer Motion', 'PWA'],
    github: 'https://github.com/SriramDivi1/TaskFlow',
    live: 'https://taskflow-app-iota.vercel.app',
    category: 'Frontend',
    featured: false,
    highlights: [
      'Calendar integration',
      'Mobile-first PWA',
      'Accessibility-focused',
      'Shadcn UI components',
    ],
    role: 'Frontend developer',
    problem: 'Task management apps often lack fluid interaction and accessibility.',
    solution: 'Built an accessible, PWA-enabled task checker with Framer Motion animations.',
    impact: 'Delivered an engaging user experience with robust offline capabilities.',
    image: null,
    features: ['Calendar view', 'PWA', 'Accessibility', 'Shadcn UI'],
    learnings: ['PWA configuration', 'Advanced Framer Motion animations'],
  },
  {
    id: 6,
    title: 'BMW M Series Landing Page',
    description: 'Premium BMW M Series landing page with interactive 3D models, dark/light mode, smooth animations & responsive design. Built with React, Three.js & Framer Motion.',
    tech: ['React', 'Three.js', 'TypeScript', 'Framer Motion'],
    github: 'https://github.com/SriramDivi1/BMW-Landing-Page',
    live: 'https://bmwgallery.vercel.app/',
    category: 'Frontend',
    featured: false,
    highlights: ['Interactive 3D models', 'Dark/light mode toggle', 'Framer Motion animations'],
    role: 'Frontend developer',
    problem: 'Needed a visually striking way to showcase automotive models online.',
    solution: 'Developed a landing page using Three.js for 3D car models and React for UI control.',
    impact: 'Created a high-conversion, premium aesthetic marketing page.',
    image: null,
    features: ['3D models', 'Dark/light mode', 'Framer Motion'],
    learnings: ['Three.js integration in React', 'Performance optimization for 3D'],
  },
  {
    id: 7,
    title: 'Fixapp',
    description: 'Application for tracking and managing fixes and tasks.',
    tech: ['TypeScript', 'React'],
    github: 'https://github.com/SriramDivi1/Fixapp',
    category: 'Frontend',
    featured: false,
    highlights: ['Task management', 'Issue tracking'],
    role: 'Frontend developer',
    problem: 'Organizations need lightweight tools for logging and assigning operational fixes.',
    solution: 'Created a swift React interface to register and monitor fixes.',
    impact: 'Streamlined task resolution through simplified UI.',
    image: null,
    features: ['Issue logging', 'Status tracking'],
    learnings: ['React component composition', 'State management'],
  },
  {
    id: 8,
    title: 'SyncPlay',
    description: 'One-stop streaming solution for movies, series, and live streams. Watch together with friends in real-time with synchronized playback and chat.',
    tech: ['TypeScript', 'React', 'Real-time'],
    github: 'https://github.com/SriramDivi1/SyncPlay',
    category: 'Full-Stack',
    featured: false,
    highlights: ['Synchronized video playback', 'Real-time chat', 'Streaming catalog'],
    role: 'Full-stack developer',
    problem: 'Watching media with remote friends suffers from sync delays.',
    solution: 'Implemented WebSocket-based real-time synchronized playback and integrated chat.',
    impact: 'Allowed seamless group viewing experiences across the internet.',
    image: null,
    features: ['Sync playback', 'Live chat', 'Movie discovery'],
    learnings: ['WebSockets synchronization', 'Video API manipulation'],
  },
  {
    id: 9,
    title: 'Expense Tracker',
    description: 'A premium expense tracking application with advanced analytics, budget management, and glassmorphism UI. Features dark mode, interactive charts, and comprehensive financial reports.',
    tech: ['React', 'Vite', 'Vanilla CSS', 'Charts'],
    github: 'https://github.com/SriramDivi1/expense-tracker-portfolio',
    live: 'https://expense-tracker-portfolio-omega.vercel.app',
    category: 'Frontend',
    featured: false,
    highlights: ['Advanced financial analytics', 'Glassmorphism aesthetic', 'Interactive charting'],
    role: 'Frontend developer',
    problem: 'Standard expense trackers lack premium visualization.',
    solution: 'Designed an analytical dashboard with rich charting libraries and elegant UI styling.',
    impact: 'Helps users effortlessly digest their spending patterns.',
    image: null,
    features: ['Analytics', 'Budget management', 'Charts', 'Glassmorphism UI'],
    learnings: ['Data visualization using charts', 'Advanced CSS styling algorithms'],
  },
  {
    id: 10,
    title: 'Mini CRM Backend',
    description: 'Production-quality REST API with NestJS, PostgreSQL, and Prisma — Authentication, RBAC, and Clean Architecture.',
    tech: ['NestJS', 'TypeScript', 'PostgreSQL', 'Prisma', 'Docker', 'JWT'],
    github: 'https://github.com/SriramDivi1/Mini-CRM-Backend-Developer-Task',
    category: 'Backend',
    featured: false,
    highlights: [
      '15+ REST API endpoints',
      'JWT Auth & RBAC (Admin/Employee)',
      'Dockerized deployment',
      'Swagger documentation',
    ],
    role: 'Backend developer',
    problem: 'Enterprise-grade CRM API with auth, roles, and clear documentation.',
    solution: 'NestJS API with JWT, RBAC, Prisma, PostgreSQL, Docker, and Swagger.',
    impact: 'Production-ready API with 15+ endpoints and full docs.',
    image: null,
    features: ['15+ REST endpoints', 'JWT & RBAC', 'Docker', 'Swagger docs'],
    timeline: 'Jan 2026',
    longDescription: 'A production-quality NestJS REST API with JWT authentication, role-based access (Admin/Employee), Prisma ORM, and full Swagger documentation. Dockerized for consistent deployment.',
    learnings: ['NestJS modules and dependency injection', 'Prisma schema design and migrations', 'RBAC with guards and decorators'],
  },
  {
    id: 11,
    title: 'ATS Resume System',
    description: 'Applicant tracking and resume parsing system for streamlined hiring workflows.',
    tech: ['JavaScript', 'Node.js'],
    github: 'https://github.com/SriramDivi1/ats-resume-system',
    category: 'Backend',
    featured: false,
    highlights: ['Resume parsing', 'Candidate tracking API'],
    role: 'Backend developer',
    problem: 'HR departments spend too much time manually parsing resumes.',
    solution: 'Created a Node.js utility to extract and index ATS-friendly data automatically.',
    impact: 'Automated the first layer of CV screening.',
    image: null,
    features: ['Document parsing', 'API endpoints'],
    learnings: ['Node.js buffer/file reading', 'Regex processing'],
  },
  {
    id: 12,
    title: 'Real-Time Chat App',
    description: 'Real-time messaging application with live updates and modern chat UX.',
    tech: ['JavaScript', 'React', 'Real-time'],
    github: 'https://github.com/SriramDivi1/real-time-chat-app',
    category: 'Full-Stack',
    featured: false,
    highlights: ['Instant messaging', 'User presence', 'Modern UI'],
    role: 'Full-stack developer',
    problem: 'Chat applications often suffer from state synchronization issues.',
    solution: 'Used WebSocket streaming mapped to React state for real-time consistency.',
    impact: 'Demonstrated proficiency in high-frequency data applications.',
    image: null,
    features: ['Live updates', 'Presence indication'],
    learnings: ['Socket.io integration', 'Concurrent state handling'],
  }
];

export const categories = ['All', 'Frontend', 'Backend', 'Full-Stack'];

